/**
 * Brand decorative layer — soft organic blobs and ripple rings in the
 * blue → teal → coral palette. Per the brand guide: organic shapes, never
 * hard rectangles as decoration, coral used sparingly. Purely presentational.
 *
 * Drop inside a `relative overflow-hidden` section. Variants position the
 * shapes for different section shapes.
 */

type Variant = 'hero' | 'soft' | 'band' | 'corner';

const Blob = ({ style }: { style: React.CSSProperties }) => (
  <div
    aria-hidden
    style={{
      position: 'absolute',
      borderRadius: '9999px',
      filter: 'blur(60px)',
      ...style,
    }}
  />
);

function Rings({ style, stroke = '#34AFFB' }: { style: React.CSSProperties; stroke?: string }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 200 200"
      style={{ position: 'absolute', ...style }}
    >
      {[92, 68, 44].map((r, i) => (
        <circle
          key={r}
          cx="100"
          cy="100"
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          opacity={0.06 + i * 0.04}
        />
      ))}
    </svg>
  );
}

export function OrganicShapes({ variant = 'soft' }: { variant?: Variant }) {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {variant === 'hero' && (
        <>
          <Blob style={{ top: '-120px', right: '-80px', width: '420px', height: '420px', background: 'rgba(52,175,251,0.18)' }} />
          <Blob style={{ bottom: '-160px', left: '-100px', width: '460px', height: '460px', background: 'rgba(112,214,197,0.16)' }} />
          <Blob style={{ top: '30%', left: '40%', width: '260px', height: '260px', background: 'rgba(255,127,102,0.07)' }} />
          <Rings style={{ top: '-40px', right: '8%', width: '260px', height: '260px' }} />
        </>
      )}

      {variant === 'soft' && (
        <>
          <Blob style={{ top: '-140px', left: '-120px', width: '380px', height: '380px', background: 'rgba(52,175,251,0.10)' }} />
          <Blob style={{ bottom: '-180px', right: '-120px', width: '420px', height: '420px', background: 'rgba(112,214,197,0.12)' }} />
        </>
      )}

      {variant === 'band' && (
        <>
          <Blob style={{ top: '-60%', left: '10%', width: '480px', height: '480px', background: 'rgba(255,255,255,0.14)' }} />
          <Blob style={{ bottom: '-70%', right: '12%', width: '520px', height: '520px', background: 'rgba(255,255,255,0.10)' }} />
          <Rings style={{ top: '-20px', left: '4%', width: '220px', height: '220px' }} stroke="#ffffff" />
        </>
      )}

      {variant === 'corner' && (
        <Blob style={{ top: '-160px', right: '-120px', width: '400px', height: '400px', background: 'rgba(52,175,251,0.10)' }} />
      )}
    </div>
  );
}
